import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../ui/button";
import { Loader2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Card, CardContent, CardHeader } from "../ui/card";
import { currencyFormatter } from "../../lib/utils";
import { FormField } from "../ui/form-field";
import {
  useGetOrderQuery,
  useUpdateServiceConfigMutation,
} from "../../store/api/orderApi";
import { toast } from "react-toastify";
import { format } from "date-fns";

const formSchema = z.object({
  id: z.any().optional(),
  type: z.any().optional(),
  allocationQuantity: z.number().min(0, "Allocation quantity must be positive"),
  deployedQuantity: z.number().min(0, "Deployed quantity must be positive"),
  cost: z.number().min(0, "Cost must be positive"),
  storageCost: z.number().min(0, "Storage cost must be positive").optional(),
  resourceId: z.string().min(1, "Resource ID is required"),
  startDate: z.string().optional(),
  stopDate: z.string().optional(),
});

const FieldDisplay = ({ label, value, classes = "text-gray-900" }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm ${classes.value}`}>{value}</p>
  </div>
);

const AllocationForm = ({ config, service, setExpandedCard }) => {
  const [updateConfig, { isLoading: isUpdatingConfig }] =
    useUpdateServiceConfigMutation();
  const classes = {
    formLabel: "text-xs",
    formItem: "space-y-1",
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: service.type === "compute" ? config.id : "",
      type: service.type !== "compute" ? config.type : "",
      allocationQuantity: config?.allocationQuantity,
      deployedQuantity: config?.deployedQuantity,
      cost: config?.cost,
      storageCost: config?.storageCost,
      resourceId: config?.resourceId,
      startDate: config?.startDate,
      stopDate: config?.stopDate,
    },
  });

  const onSubmit = async (values) => {
    try {
      const res = await updateConfig({
        serviceId: service.id,
        ...values,
      }).unwrap();
      if (res.statusCode === 200) toast.success(res.message);
    } catch (error) {
      console.error("Failed to update compute allocation:", error);
    }
    setExpandedCard(null);
    reset();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 mt-4 pt-4 border-t"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <FormField
          label="Allocation Quantity"
          name="allocationQuantity"
          type="number"
          register={register}
          errors={errors}
          classes={classes}
        />
        <FormField
          label="Deployed Quantity"
          name="deployedQuantity"
          type="number"
          register={register}
          errors={errors}
          classes={classes}
        />
        <FormField
          label="Cost (₹)"
          name="cost"
          type="number"
          step="0.01"
          register={register}
          errors={errors}
          classes={classes}
        />
        {service.type === "compute" && (
          <FormField
            label="Storage Cost"
            name="storageCost"
            type="number"
            step="0.01"
            register={register}
            errors={errors}
            classes={classes}
          />
        )}
        <FormField
          label="Resource ID"
          name="resourceId"
          type="text"
          placeholder="Instance ID or ARN"
          register={register}
          errors={errors}
          classes={classes}
        />
        <FormField
          label="Start Date"
          name="startDate"
          type="date"
          register={register}
          control={control}
          errors={errors}
          classes={classes}
        />
        <FormField
          label="Stop Date"
          name="stopDate"
          type="date"
          register={register}
          control={control}
          errors={errors}
          classes={classes}
        />
      </div>
      <div className="flex justify-end space-x-2">
        <Button
          variant="outline"
          type="button"
          size="sm"
          onClick={() => setExpandedCard(null)}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm">
          Save
        </Button>
      </div>
    </form>
  );
};

const ComputeAllocationModal = ({ isOpen, onClose, id }) => {
  const { data, isLoading, error } = useGetOrderQuery(id);
  const [expandedCard, setExpandedCard] = useState(null);

  const handleCardClick = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compute Allocation</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">Loading data...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            Error loading order data. Please try again.
          </div>
        ) : (
          <div className="w-full mx-auto bg-white rounded-lg overflow-hidden">
            {/* Services Section */}
            <div className="space-y-6">
              {(data.services || []).map((service, serviceIndex) => (
                <div key={serviceIndex}>
                  <h3 className="text-sm text-blue-600 font-semibold mb-2 capitalize">
                    {service.type} Services
                  </h3>

                  <div className="space-y-4">
                    {service.config.map((item, itemIndex) => {
                      const cardIndex = `${service.id}-${service.type}-${itemIndex}`;
                      const isExpanded = expandedCard === cardIndex;
                      return (
                        <Card
                          key={itemIndex}
                          className={`py-4 gap-4 ${
                            isExpanded ? "border-blue-600" : ""
                          }`}
                        >
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4">
                            <span className="text-sm font-medium">
                              {service.type === "compute" ? item.id : item.type}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleCardClick(cardIndex)}
                            >
                              <Pencil className="w-4" />
                            </Button>
                          </CardHeader>
                          <CardContent className="px-4">
                            {service.type === "compute" && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <FieldDisplay
                                  label="ID"
                                  value={item?.id || "-"}
                                />
                                <FieldDisplay
                                  label="Quantity"
                                  value={item?.quantity || "-"}
                                />
                                <FieldDisplay
                                  label="Type"
                                  value={item?.subscriptionType || "-"}
                                />
                                <FieldDisplay
                                  label="Max Hours"
                                  value={item?.maxHours || "-"}
                                />
                                <FieldDisplay
                                  label="Storage"
                                  value={item?.Storage || "-"}
                                />
                                <FieldDisplay
                                  label="Allocation Quantity"
                                  value={item.allocationQuantity || "-"}
                                />
                                <FieldDisplay
                                  label="Deployed Quantity"
                                  value={item.deployedQuantity || "-"}
                                />
                                <FieldDisplay
                                  label="Cost"
                                  classes={{ value: "font-medium" }}
                                  value={`${
                                    item.cost
                                      ? currencyFormatter(item.cost)
                                      : "-"
                                  }`}
                                />
                                <FieldDisplay
                                  label="Storage Cost"
                                  classes={{ value: "font-medium" }}
                                  value={`${
                                    item.storageCost
                                      ? currencyFormatter(item.storageCost)
                                      : "-"
                                  }`}
                                />
                                <FieldDisplay
                                  label="Resource ID"
                                  value={item.resourceId || "-"}
                                  valueClassName="font-medium"
                                />
                                <FieldDisplay
                                  label="Start Date"
                                  value={
                                    item.startDate
                                      ? format(item.startDate, "dd-MM-yyyy")
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Stop Date"
                                  value={
                                    item.stopDate
                                      ? format(item.stopDate, "dd-MM-yyyy")
                                      : "-"
                                  }
                                />
                              </div>
                            )}
                            {service.type === "storage" && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <FieldDisplay
                                  label="Type"
                                  value={item?.type || "-"}
                                />
                                <FieldDisplay
                                  label="Quantity (GB)"
                                  value={item?.quantity || "-"}
                                />
                                <FieldDisplay
                                  label="Months"
                                  value={item?.months || "-"}
                                />
                                <FieldDisplay
                                  label="Allocation Quantity"
                                  value={
                                    item.allocationQuantity
                                      ? item.allocationQuantity
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Deployed Quantity"
                                  value={
                                    item.deployedQuantity
                                      ? item.deployedQuantity
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Cost"
                                  value={`${
                                    item.cost
                                      ? currencyFormatter(item.cost)
                                      : "-"
                                  }`}
                                  classes={{ value: "font-medium" }}
                                />
                                <FieldDisplay
                                  label="Resource ID"
                                  value={
                                    item.resourceId ? item.resourceId : "-"
                                  }
                                  classes={{ value: "font-medium" }}
                                />
                                <FieldDisplay
                                  label="Start Date"
                                  value={
                                    item.startDate
                                      ? new Date(
                                          item.startDate
                                        ).toLocaleDateString()
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Stop Date"
                                  value={
                                    item.stopDate
                                      ? new Date(
                                          item.stopDate
                                        ).toLocaleDateString()
                                      : "-"
                                  }
                                />
                              </div>
                            )}
                            {service.type === "platform" && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <FieldDisplay
                                  label="Type"
                                  value={item?.type || "-"}
                                />
                                <FieldDisplay
                                  label="Discount (%)"
                                  value={item?.discount || "-"}
                                />
                                <FieldDisplay
                                  label="Allocation Quantity"
                                  value={
                                    item.allocationQuantity
                                      ? item.allocationQuantity
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Deployed Quantity"
                                  value={
                                    item.deployedQuantity
                                      ? item.deployedQuantity
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Cost"
                                  value={`${
                                    item.cost
                                      ? currencyFormatter(item.cost)
                                      : "-"
                                  }`}
                                  classes={{ value: "font-medium" }}
                                />
                                <FieldDisplay
                                  label="Resource ID"
                                  value={
                                    item.resourceId ? item.resourceId : "-"
                                  }
                                  classes={{ value: "font-medium" }}
                                />
                                <FieldDisplay
                                  label="Start Date"
                                  value={
                                    item.startDate
                                      ? new Date(
                                          item.startDate
                                        ).toLocaleDateString()
                                      : "-"
                                  }
                                />
                                <FieldDisplay
                                  label="Stop Date"
                                  value={
                                    item.stopDate
                                      ? new Date(
                                          item.stopDate
                                        ).toLocaleDateString()
                                      : "-"
                                  }
                                />
                              </div>
                            )}
                            {isExpanded && (
                              <AllocationForm
                                service={service}
                                config={item}
                                setExpandedCard={setExpandedCard}
                              />
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComputeAllocationModal;
